import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, formatCurrency } from '../../utils/api'
import ConditionPicker, { REPAIR_CONDITION_OPTIONS } from './ConditionPicker'
import DealLink from '../shared/DealLink.jsx'
import QueueHeader from '../shared/QueueHeader.jsx'

// Statuses kept out of the default work list. They stay reachable through their
// own filter chips; one list drives both the filter and the counts so the two
// can never disagree.
const HIDDEN_FROM_DEFAULT = ['Queued for Photography', 'Listed']

// Design System (matching original warehouse-system)
const theme = {
  colors: {
    charcoal: '#222725',
    sunburst: '#FFD44D',
    greenOnions: '#3BB273',
    imBlue: '#4E8098',
    crimson: '#E5625E',
    porcelain: '#F2EDE4',
    white: '#FFFFFF',
    grey100: '#F8F9F8',
    grey200: '#E7E5E4',
    grey300: '#D6D3D1',
    grey400: '#A8A29E',
    grey500: '#78716C',
    greenLight: '#E8F5EE',
    amberLight: '#FFF9E6',
    redLight: '#FCEEED',
    purple: '#8B5CF6',
    purpleLight: '#F5F3FF',
    blueLight: '#EDF3F6'
  },
  fonts: { sans: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif" },
  radius: { sm: '6px', md: '10px', lg: '16px' },
  shadows: { sm: '0 1px 2px rgba(0,0,0,0.05)', md: '0 4px 12px rgba(0,0,0,0.08)' }
}

// Components
const Card = ({ children, style = {}, padding = true }) => (
  <div style={{
    background: theme.colors.white,
    borderRadius: theme.radius.lg,
    boxShadow: theme.shadows.sm,
    border: `1px solid ${theme.colors.grey200}`,
    padding: padding ? '24px' : 0,
    ...style
  }}>
    {children}
  </div>
)

const Badge = ({ children, color = 'grey' }) => {
  const colors = {
    grey: { bg: theme.colors.grey200, text: theme.colors.grey500 },
    green: { bg: theme.colors.greenLight, text: theme.colors.greenOnions },
    amber: { bg: theme.colors.amberLight, text: '#B8860B' },
    red: { bg: theme.colors.redLight, text: theme.colors.crimson },
    purple: { bg: theme.colors.purpleLight, text: theme.colors.purple },
    blue: { bg: theme.colors.blueLight, text: theme.colors.imBlue }
  }
  const c = colors[color] || colors.grey
  return (
    <span style={{
      display: 'inline-flex',
      padding: '4px 10px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: 600,
      background: c.bg,
      color: c.text
    }}>
      {children}
    </span>
  )
}

const Button = ({ children, variant = 'primary', size = 'md', fullWidth = false, disabled = false, onClick }) => {
  const variants = {
    primary: { background: theme.colors.sunburst, color: theme.colors.charcoal },
    secondary: { background: theme.colors.white, color: theme.colors.charcoal, border: `1px solid ${theme.colors.grey300}` },
    success: { background: theme.colors.greenOnions, color: theme.colors.white },
    danger: { background: theme.colors.crimson, color: theme.colors.white },
    ghost: { background: 'transparent', color: theme.colors.grey500 }
  }
  const sizes = {
    sm: { padding: '8px 14px', fontSize: '13px' },
    md: { padding: '12px 20px', fontSize: '14px' },
    lg: { padding: '16px 28px', fontSize: '16px' }
  }
  const v = variants[variant]
  const s = sizes[size]
  return (
    <button onClick={onClick} disabled={disabled} style={{
      ...v, ...s,
      border: v.border || 'none',
      borderRadius: theme.radius.md,
      fontWeight: 600,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      width: fullWidth ? '100%' : 'auto',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    }}>
      {children}
    </button>
  )
}

const Spinner = ({ size = 20 }) => (
  <div style={{
    width: size, height: size,
    border: `2px solid ${theme.colors.grey200}`,
    borderTopColor: theme.colors.charcoal,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite'
  }}>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
)

// A repair added from a Shopify product carries `SHOPIFY-<id>`, which is not a
// deal, so it stays plain text. A real deal opens in a new tab.
const SHOPIFY_REPAIR_PREFIX = 'SHOPIFY-'

function RepairDealLink({ dealId }) {
  if (!dealId || String(dealId).startsWith(SHOPIFY_REPAIR_PREFIX)) return dealId || '-'
  return <DealLink dealId={dealId} newTab />
}

function Repairs() {
  const navigate = useNavigate()
  const [allItems, setAllItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('')
  const [expandedItem, setExpandedItem] = useState(null)
  const [repairNotes, setRepairNotes] = useState('')
  const [repairCost, setRepairCost] = useState('')
  const [postRepairCondition, setPostRepairCondition] = useState('')
  const [updating, setUpdating] = useState(null)
  const [costModal, setCostModal] = useState(null) // { item, newStatus }

  // Technician assignment state
  const [technicians, setTechnicians] = useState([])
  const [assignModal, setAssignModal] = useState(null) // repair item to assign
  const [selectedTechId, setSelectedTechId] = useState('')

  // Add Repair modal state
  const [addModal, setAddModal] = useState(false)
  const [addSource, setAddSource] = useState('hubspot') // 'hubspot' | 'shopify'
  const [addLookupId, setAddLookupId] = useState('')
  const [addLooking, setAddLooking] = useState(false)
  const [addLookupResult, setAddLookupResult] = useState(null) // { type, items, dealId, dealName }
  const [addLookupError, setAddLookupError] = useState('')
  const [addSelectedItems, setAddSelectedItems] = useState({}) // { index: { ...item, issueDescription } }
  const [addSubmitting, setAddSubmitting] = useState(false)
  const [toast, setToast] = useState(null)

  useEffect(() => {
    loadItems()
    loadTechnicians()
  }, [])

  async function loadItems() {
    setLoading(true)
    try {
      // Fetch every status (?all=1) so the chips can reveal and count the
      // queued/listed items the default list hides. Without this the backend
      // omits them and the 'Listed' chip is permanently empty.
      const response = await api.get('/api/warehouse/repairs?all=1')
      setAllItems(response.items || [])
    } catch (error) {
      console.error('Failed to load repair items:', error)
    } finally {
      setLoading(false)
    }
  }

  // Filter items client-side. A specific chip shows exactly its status; the
  // default view is the active work list, with queued/listed held back.
  const items = statusFilter
    ? allItems.filter(i => i.status === statusFilter)
    : allItems.filter(i => !HIDDEN_FROM_DEFAULT.includes(i.status))

  async function loadTechnicians() {
    try {
      const response = await api.get('/api/warehouse/technicians')
      setTechnicians(response.technicians || [])
    } catch (err) {
      console.error('Failed to load technicians:', err)
    }
  }

  function openAddModal() {
    setAddModal(true)
    setAddSource('hubspot')
    setAddLookupId('')
    setAddLooking(false)
    setAddLookupResult(null)
    setAddLookupError('')
    setAddSelectedItems({})
    setAddSubmitting(false)
  }

  function closeAddModal() {
    setAddModal(false)
    setAddLookupResult(null)
    setAddLookupError('')
    setAddSelectedItems({})
  }

  function parseParsedItems(parsedItemsStr) {
    if (!parsedItemsStr) return []
    return parsedItemsStr.split('\n').filter(l => l.trim()).map((line, idx) => {
      const withoutNumber = line.replace(/^\d+\.\s*/, '')
      const spMatch = withoutNumber.match(/\bSP[-:.]?\s*£?([\d.]+)/i)
      const bpMatch = withoutNumber.match(/\bBP[-:.]?\s*£?([\d.]+)/i)
      const qtyMatch = withoutNumber.match(/Qty:(\d+)/i)
      const name = withoutNumber
        .replace(/\s*-\s*Qty:\d+/i, '')
        .replace(/\s*-?\s*\bSP[-:.]?\s*£?[\d.]+/i, '')
        .replace(/\s*-?\s*\bBP[-:.]?\s*£?[\d.]+/i, '')
        .replace(/\s*-\s*Sc:\d+/i, '')
        .trim()
      return {
        index: idx,
        name: name || `Item ${idx + 1}`,
        sellPrice: spMatch ? parseFloat(spMatch[1]) : 0,
        buyPrice: bpMatch ? parseFloat(bpMatch[1]) : 0,
        quantity: qtyMatch ? parseInt(qtyMatch[1]) : 1
      }
    })
  }

  async function doLookup() {
    const id = addLookupId.trim()
    if (!id) return

    setAddLooking(true)
    setAddLookupError('')
    setAddLookupResult(null)
    setAddSelectedItems({})

    try {
      if (addSource === 'hubspot') {
        const response = await api.get(`/api/deals/${id}`)
        if (!response.success || !response.deal) {
          setAddLookupError('Deal not found')
          return
        }
        const deal = response.deal
        const items = parseParsedItems(deal.parsed_items)
        if (items.length === 0) {
          setAddLookupError('No parsed items found on this deal')
          return
        }
        setAddLookupResult({
          type: 'hubspot',
          dealId: deal.id,
          dealName: deal.dealname || `Deal ${deal.id}`,
          items
        })
      } else {
        const response = await api.get(`/api/inventory/shopify/product/${id}`)
        if (!response.success || !response.product) {
          setAddLookupError('Product not found')
          return
        }
        const product = response.product
        const variant = product.variants?.[0]
        const items = [{
          index: 0,
          name: product.title,
          sellPrice: variant?.price ? parseFloat(variant.price) : 0,
          buyPrice: product.cost ? parseFloat(product.cost) : (variant?.cost ? parseFloat(variant.cost) : 0),
          productType: product.product_type || '',
          quantity: 1
        }]
        setAddLookupResult({
          type: 'shopify',
          dealId: `${SHOPIFY_REPAIR_PREFIX}${product.id}`,
          dealName: product.title,
          items
        })
        // Auto-select the single product
        setAddSelectedItems({ 0: { ...items[0], issueDescription: '' } })
      }
    } catch (err) {
      console.error('Lookup failed:', err)
      setAddLookupError(err.message || 'Lookup failed')
    } finally {
      setAddLooking(false)
    }
  }

  function toggleAddItem(item) {
    setAddSelectedItems(prev => {
      const copy = { ...prev }
      if (copy[item.index]) {
        delete copy[item.index]
      } else {
        copy[item.index] = { ...item, issueDescription: '' }
      }
      return copy
    })
  }

  function setItemIssue(index, value) {
    setAddSelectedItems(prev => ({
      ...prev,
      [index]: { ...prev[index], issueDescription: value }
    }))
  }

  async function submitAddRepairs() {
    const selected = Object.values(addSelectedItems)
    if (selected.length === 0) {
      alert('Please select at least one item')
      return
    }
    const missing = selected.filter(i => !i.issueDescription?.trim())
    if (missing.length > 0) {
      alert('Please describe the issue for all selected items')
      return
    }

    setAddSubmitting(true)
    try {
      const items = selected.map(item => ({
        dealId: addLookupResult.dealId,
        itemIndex: item.index,
        itemName: item.name,
        productType: item.productType || null,
        buyPrice: item.buyPrice || 0,
        sellPrice: item.sellPrice || 0,
        issueDescription: item.issueDescription.trim()
      }))

      const response = await api.post('/api/warehouse/repairs/add', { items })
      if (response.success) {
        closeAddModal()
        await loadItems()
      } else {
        alert('Failed to add repairs')
      }
    } catch (err) {
      console.error('Failed to add repairs:', err)
      alert('Failed to add repairs: ' + (err.message || 'Unknown error'))
    } finally {
      setAddSubmitting(false)
    }
  }

  function openAssignModal(item) {
    setAssignModal(item)
    setSelectedTechId('')
  }

  async function assignAndStart() {
    if (!selectedTechId) {
      alert('Please select a technician')
      return
    }
    const tech = technicians.find(t => t.id === parseInt(selectedTechId))
    setUpdating(assignModal.id)
    try {
      await api.patch(`/api/warehouse/repairs/${assignModal.id}`, {
        status: 'In Progress',
        technician_id: parseInt(selectedTechId),
        technician_name: tech?.name || ''
      })
      await loadItems()
      setAssignModal(null)
      setExpandedItem(null)
    } catch (error) {
      console.error('Failed to assign technician:', error)
      alert('Failed to assign technician and start repair')
    } finally {
      setUpdating(null)
    }
  }

  function openCostModal(item, newStatus) {
    setCostModal({ item, newStatus })
    setRepairCost('')
  }

  async function submitWithCost() {
    if (!costModal) return
    const { item, newStatus } = costModal
    setUpdating(item.id)
    try {
      await api.patch(`/api/warehouse/repairs/${item.id}`, {
        status: newStatus,
        repairNotes: repairNotes || undefined,
        repair_cost: parseFloat(repairCost) || 0,
        ...(newStatus === 'Complete' ? { post_repair_condition: postRepairCondition || null } : {})
      })
      await loadItems()
      setCostModal(null)
      setExpandedItem(null)
      setRepairNotes('')
      setRepairCost('')
      setPostRepairCondition('')
    } catch (error) {
      console.error('Failed to update repair status:', error)
      alert('Failed to update repair status')
    } finally {
      setUpdating(null)
    }
  }

  async function updateStatus(item, newStatus) {
    setUpdating(item.id)
    try {
      await api.patch(`/api/warehouse/repairs/${item.id}`, {
        status: newStatus,
        repairNotes: (newStatus === 'Complete' || newStatus === 'Not Repaired') ? repairNotes : undefined
      })
      await loadItems()
      setExpandedItem(null)
      setRepairNotes('')
    } catch (error) {
      console.error('Failed to update repair status:', error)
      alert('Failed to update repair status')
    } finally {
      setUpdating(null)
    }
  }

  function showToast(message, type = 'success') {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3000)
  }

  async function createListing(item) {
    setUpdating(item.id)
    try {
      const response = await api.post('/api/warehouse/repairs/queue-photography', {
        repairIds: [item.id]
      })
      if (response.success) {
        showToast('Queued for photography')
        await loadItems()
      }
    } catch (error) {
      console.error('Failed to queue for photography:', error)
      showToast('Failed to queue for photography', 'error')
    } finally {
      setUpdating(null)
    }
  }

  function finishInPhotography(item) {
    // Deep-link straight into this item's photo editor. Falls back to the queue
    // list if the queue row can't be resolved (shouldn't happen for a queued item).
    navigate(item.photography_queue_id
      ? `/warehouse/photography?item=${item.photography_queue_id}`
      : '/warehouse/photography')
  }

  async function returnToRepairs(item) {
    if (!confirm('Return this item to Repairs? The photos taken for it will be discarded, and you can re-queue it for photography later.')) return
    setUpdating(item.id)
    try {
      await api.post(`/api/warehouse/repairs/${item.id}/return-to-repairs`)
      showToast('Returned to repairs')
      await loadItems()
      setExpandedItem(null)
    } catch (error) {
      console.error('Failed to return to repairs:', error)
      showToast('Failed to return to repairs', 'error')
    } finally {
      setUpdating(null)
    }
  }

  async function disposeItem(item) {
    if (!confirm('Are you sure you want to dispose of this item? It will not be listed for sale.')) return
    setUpdating(item.id)
    try {
      await api.patch(`/api/warehouse/repairs/${item.id}`, { status: 'Disposed' })
      showToast('Item disposed')
      await loadItems()
      setExpandedItem(null)
    } catch (error) {
      console.error('Failed to dispose item:', error)
      showToast('Failed to dispose item', 'error')
    } finally {
      setUpdating(null)
    }
  }

  function toggleExpand(item) {
    if (expandedItem === item.id) {
      setExpandedItem(null)
      setRepairNotes('')
    } else {
      setExpandedItem(item.id)
      setRepairNotes(item.repair_notes || '')
    }
  }

  function normalizeStatus(status) {
    return (status || '').toLowerCase().replace(/\s+/g, '_')
  }

  function getStatusBadgeColor(status) {
    const s = normalizeStatus(status)
    if (s === 'complete') return 'green'
    if (s === 'in_progress') return 'amber'
    if (s === 'not_repaired') return 'red'
    if (s === 'queued_for_photography') return 'blue'
    if (s === 'listed') return 'purple'
    if (s === 'disposed') return 'grey'
    return 'grey'
  }

  const statusCounts = {
    // 'All' is the active work list, so its count excludes the held-back
    // statuses — matching what the default view actually shows.
    all: allItems.filter(i => !HIDDEN_FROM_DEFAULT.includes(i.status)).length,
    not_started: allItems.filter(i => normalizeStatus(i.status) === 'not_started').length,
    in_progress: allItems.filter(i => normalizeStatus(i.status) === 'in_progress').length,
    complete: allItems.filter(i => normalizeStatus(i.status) === 'complete').length,
    not_repaired: allItems.filter(i => normalizeStatus(i.status) === 'not_repaired').length,
    queued_for_photography: allItems.filter(i => normalizeStatus(i.status) === 'queued_for_photography').length,
    listed: allItems.filter(i => normalizeStatus(i.status) === 'listed').length,
    disposed: allItems.filter(i => normalizeStatus(i.status) === 'disposed').length
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spinner size={40} />
      </div>
    )
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px' }}>
      <QueueHeader title="Repairs" description="Manage items needing repair">
        <Button variant="primary" onClick={openAddModal}>+ Add Repair</Button>
      </QueueHeader>

      {/* Status Filter Header */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ fontSize: '14px', color: theme.colors.grey500, marginBottom: '12px' }}>
          Filter by status:
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {[
            { key: '', label: 'All', count: statusCounts.all, color: theme.colors.imBlue },
            { key: 'Not Started', count: statusCounts.not_started, color: theme.colors.grey400 },
            { key: 'In Progress', count: statusCounts.in_progress, color: theme.colors.sunburst },
            { key: 'Complete', count: statusCounts.complete, color: theme.colors.greenOnions },
            { key: 'Not Repaired', count: statusCounts.not_repaired, color: theme.colors.crimson },
            { key: 'Queued for Photography', label: 'Queued for Photo', count: statusCounts.queued_for_photography, color: theme.colors.imBlue },
            { key: 'Listed', count: statusCounts.listed, color: theme.colors.purple },
            { key: 'Disposed', count: statusCounts.disposed, color: theme.colors.grey400 }
          ].map((stat, idx) => (
            <button
              key={idx}
              onClick={() => setStatusFilter(stat.key)}
              style={{
                padding: '8px 16px',
                borderRadius: '20px',
                border: statusFilter === stat.key ? `2px solid ${stat.color}` : `1px solid ${theme.colors.grey300}`,
                background: statusFilter === stat.key ? `${stat.color}15` : theme.colors.white,
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
                color: statusFilter === stat.key ? stat.color : theme.colors.charcoal,
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              {stat.label || stat.key}
              <span style={{
                background: statusFilter === stat.key ? stat.color : theme.colors.grey200,
                color: statusFilter === stat.key ? theme.colors.white : theme.colors.grey500,
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 600
              }}>
                {stat.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Current Filter */}
      {statusFilter && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 600 }}>{statusFilter} Repairs</h2>
          <Button variant="ghost" size="sm" onClick={() => setStatusFilter('')}>Clear Filter</Button>
        </div>
      )}

      {/* Items List */}
      {items.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔧</div>
          <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>No Repairs</h3>
          <p style={{ color: theme.colors.grey500 }}>
            {statusFilter ? 'No repairs with this status.' : 'No items need repair.'}
          </p>
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {items.map(item => {
            const status = normalizeStatus(item.status)
            const isExpanded = expandedItem === item.id
            const itemName = item.item_name || item.product_name || item.name || `Item ${item.item_index}`

            return (
              <Card
                key={item.id}
                padding={false}
                style={{
                  border: isExpanded ? `2px solid ${theme.colors.purple}` : `1px solid ${theme.colors.grey200}`,
                  overflow: 'hidden'
                }}
              >
                {/* Item Header */}
                <div
                  onClick={() => toggleExpand(item)}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '20px', cursor: 'pointer',
                    background: isExpanded ? theme.colors.purpleLight : 'transparent'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px' }}>
                      <span style={{ fontWeight: 600, fontSize: '16px' }}>{itemName}</span>
                      <Badge color={getStatusBadgeColor(item.status)}>
                        {item.status || 'Not Started'}
                      </Badge>
                    </div>
                    <div style={{ fontSize: '13px', color: theme.colors.grey500 }}>
                      Deal: <RepairDealLink dealId={item.deal_id} /> • {item.issue_description || 'No description'}
                      {item.technician_name && (
                        <span style={{ marginLeft: '8px', color: theme.colors.imBlue, fontWeight: 500 }}>
                          — {item.technician_name}
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <div style={{ textAlign: 'right' }}>
                      {item.sell_price && (
                        <div style={{ fontWeight: 600 }}>Sell: {formatCurrency(item.sell_price)}</div>
                      )}
                      {item.buy_price && (
                        <div style={{ fontSize: '13px', color: theme.colors.grey500 }}>
                          Buy: {formatCurrency(item.buy_price)}
                        </div>
                      )}
                      {item.repair_cost > 0 && (
                        <div style={{ fontSize: '13px', color: theme.colors.imBlue }}>
                          Repair: {formatCurrency(item.repair_cost)}
                        </div>
                      )}
                    </div>
                    <div style={{
                      fontSize: '14px', color: theme.colors.grey400,
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)',
                      transition: 'transform 0.2s'
                    }}>
                      ▼
                    </div>
                  </div>
                </div>

                {/* Expanded Section */}
                {isExpanded && (
                  <div style={{ padding: '20px', borderTop: `1px solid ${theme.colors.grey200}`, background: theme.colors.grey100 }}>
                    {/* Issue Details */}
                    <div style={{ marginBottom: '20px' }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: theme.colors.grey500, marginBottom: '8px' }}>
                        Issue Description
                      </div>
                      <div style={{
                        padding: '14px',
                        background: theme.colors.white,
                        borderRadius: theme.radius.md,
                        border: `1px solid ${theme.colors.grey200}`
                      }}>
                        {item.issue_description || 'No description provided'}
                      </div>
                    </div>

                    {/* Repair Notes */}
                    {(status === 'in_progress' || status === 'complete' || status === 'not_repaired' || item.repair_notes) && (
                      <div style={{ marginBottom: '20px' }}>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: theme.colors.grey500, marginBottom: '8px' }}>
                          Repair Notes
                        </div>
                        <textarea
                          value={repairNotes}
                          onChange={e => setRepairNotes(e.target.value)}
                          placeholder="Enter repair notes..."
                          rows={3}
                          disabled={status === 'complete'}
                          style={{
                            width: '100%', padding: '12px',
                            border: `1px solid ${theme.colors.grey300}`,
                            borderRadius: theme.radius.md, fontSize: '14px',
                            resize: 'vertical', outline: 'none',
                            background: status === 'complete' ? theme.colors.grey100 : theme.colors.white
                          }}
                        />
                      </div>
                    )}

                    {/* Spares/Repair Info for Not Repaired items */}
                    {status === 'not_repaired' && (
                      <div style={{
                        marginBottom: '20px',
                        padding: '14px',
                        background: theme.colors.redLight,
                        borderRadius: theme.radius.md,
                        border: `1px solid ${theme.colors.crimson}20`
                      }}>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: theme.colors.crimson, marginBottom: '8px' }}>
                          Will be listed as: Spares or Repair
                        </div>
                        <div style={{ fontSize: '13px', color: theme.colors.grey500 }}>
                          <strong>Issue:</strong> {item.issue_description || 'No issue description - please add one before listing'}
                        </div>
                        <div style={{ fontSize: '13px', color: theme.colors.grey500, marginTop: '4px' }}>
                          <strong>Listing price:</strong> {formatCurrency((item.sell_price || 0) * 0.10)} (10% of sell price)
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                      {status === 'not_started' && (
                        <Button
                          variant="primary"
                          disabled={updating === item.id}
                          onClick={() => openAssignModal(item)}
                        >
                          {updating === item.id ? <Spinner size={18} /> : 'Start Repair'}
                        </Button>
                      )}

                      {status === 'in_progress' && (
                        <>
                          <Button
                            variant="danger"
                            disabled={updating === item.id}
                            onClick={() => openCostModal(item, 'Not Repaired')}
                          >
                            Cannot Repair
                          </Button>
                          <Button
                            variant="success"
                            disabled={updating === item.id}
                            onClick={() => openCostModal(item, 'Complete')}
                          >
                            {updating === item.id ? <Spinner size={18} /> : 'Mark Complete'}
                          </Button>
                        </>
                      )}

                      {status === 'complete' && (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                          <Button
                            variant="primary"
                            disabled={updating === item.id || !item.post_repair_condition}
                            onClick={() => createListing(item)}
                          >
                            {updating === item.id ? <Spinner size={18} /> : 'Queue for Photography'}
                          </Button>
                          {!item.post_repair_condition && (
                            <span style={{ fontSize: '11px', color: theme.colors.grey400 }}>
                              Set condition first (click Mark Complete to edit)
                            </span>
                          )}
                        </div>
                      )}

                      {status === 'not_repaired' && (
                        <>
                          <Button
                            variant="secondary"
                            disabled={updating === item.id}
                            onClick={() => disposeItem(item)}
                          >
                            Dispose
                          </Button>
                          <Button
                            variant="danger"
                            disabled={updating === item.id}
                            onClick={() => createListing(item)}
                          >
                            {updating === item.id ? <Spinner size={18} /> : 'Queue for Photography'}
                          </Button>
                        </>
                      )}

                      {status === 'queued_for_photography' && (
                        <>
                          <Button
                            variant="secondary"
                            disabled={updating === item.id}
                            onClick={() => returnToRepairs(item)}
                          >
                            Return to Repairs
                          </Button>
                          <Button
                            variant="primary"
                            disabled={updating === item.id}
                            onClick={() => finishInPhotography(item)}
                          >
                            {updating === item.id ? <Spinner size={18} /> : 'Finish in Photography →'}
                          </Button>
                        </>
                      )}

                      {status === 'listed' && (
                        <div style={{
                          padding: '12px 20px',
                          background: theme.colors.purpleLight,
                          borderRadius: theme.radius.md,
                          color: theme.colors.purple,
                          fontWeight: 500,
                          fontSize: '14px'
                        }}>
                          ✓ Listed on Shopify
                        </div>
                      )}

                      {status === 'disposed' && (
                        <div style={{
                          padding: '12px 20px',
                          background: theme.colors.grey100,
                          borderRadius: theme.radius.md,
                          color: theme.colors.grey500,
                          fontWeight: 500,
                          fontSize: '14px'
                        }}>
                          Disposed
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </Card>
            )
          })}
        </div>
      )}
      {/* Repair Cost Modal */}
      {costModal && (() => {
        const itemName = costModal.item.item_name || costModal.item.product_name || `Item ${costModal.item.item_index}`
        const isComplete = costModal.newStatus === 'Complete'

        return (
          <div
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
            }}
            onClick={() => { setCostModal(null); setPostRepairCondition('') }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: theme.colors.white, borderRadius: theme.radius.lg,
                maxWidth: '460px', width: '90%', boxShadow: theme.shadows.md
              }}
            >
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '20px', borderBottom: `1px solid ${theme.colors.grey200}`
              }}>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
                  {isComplete ? 'Mark Complete' : 'Cannot Repair'}
                </h2>
                <button
                  onClick={() => { setCostModal(null); setPostRepairCondition('') }}
                  style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: theme.colors.grey400 }}
                >
                  &times;
                </button>
              </div>

              <div style={{ padding: '20px' }}>
                <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px' }}>{itemName}</div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: theme.colors.grey500, marginBottom: '8px' }}>
                    Repair Cost
                  </label>
                  <div style={{ position: 'relative' }}>
                    <span style={{
                      position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
                      fontSize: '14px', color: theme.colors.grey500
                    }}>£</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={repairCost}
                      onChange={e => setRepairCost(e.target.value)}
                      placeholder="0.00"
                      autoFocus
                      style={{
                        width: '100%', padding: '10px 12px 10px 28px', fontSize: '14px',
                        border: `1px solid ${theme.colors.grey300}`, borderRadius: theme.radius.md,
                        outline: 'none'
                      }}
                    />
                  </div>
                </div>

                {isComplete && (
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: theme.colors.grey500, marginBottom: '10px' }}>
                      Post-Repair Condition *
                    </label>
                    <ConditionPicker
                      options={REPAIR_CONDITION_OPTIONS}
                      value={postRepairCondition}
                      onChange={setPostRepairCondition}
                    />
                  </div>
                )}

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <Button variant="secondary" onClick={() => { setCostModal(null); setPostRepairCondition('') }}>Cancel</Button>
                  <Button
                    variant={isComplete ? 'success' : 'danger'}
                    disabled={updating === costModal.item.id || (isComplete && !postRepairCondition)}
                    onClick={submitWithCost}
                  >
                    {updating === costModal.item.id ? <Spinner size={18} /> : (isComplete ? 'Mark Complete' : 'Cannot Repair')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )
      })()}

      {/* Technician Assignment Modal */}
      {assignModal && (() => {
        const itemProductType = assignModal.product_type || ''
        const matchingTechs = technicians.filter(t =>
          t.active && (
            !itemProductType ||
            t.product_types.length === 0 ||
            t.product_types.includes(itemProductType)
          )
        )
        const itemName = assignModal.item_name || assignModal.product_name || `Item ${assignModal.item_index}`

        return (
          <div
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
            }}
            onClick={() => setAssignModal(null)}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: theme.colors.white, borderRadius: theme.radius.lg,
                maxWidth: '450px', width: '90%', boxShadow: theme.shadows.md
              }}
            >
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '20px', borderBottom: `1px solid ${theme.colors.grey200}`
              }}>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>Assign Technician</h2>
                <button
                  onClick={() => setAssignModal(null)}
                  style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: theme.colors.grey400 }}
                >
                  &times;
                </button>
              </div>

              <div style={{ padding: '20px' }}>
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>{itemName}</div>
                  {itemProductType && (
                    <Badge color="blue">{itemProductType}</Badge>
                  )}
                </div>

                {matchingTechs.length === 0 ? (
                  <div style={{
                    padding: '16px', background: theme.colors.amberLight,
                    borderRadius: theme.radius.md, fontSize: '14px', color: '#B8860B',
                    marginBottom: '16px'
                  }}>
                    No active technicians available{itemProductType ? ` for "${itemProductType}"` : ''}. Please add a technician in Admin settings.
                  </div>
                ) : (
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: theme.colors.grey500, marginBottom: '8px' }}>
                      Select Technician
                    </label>
                    <select
                      value={selectedTechId}
                      onChange={e => setSelectedTechId(e.target.value)}
                      style={{
                        width: '100%', padding: '10px 12px', fontSize: '14px',
                        border: `1px solid ${theme.colors.grey300}`, borderRadius: theme.radius.md,
                        background: theme.colors.white, outline: 'none'
                      }}
                    >
                      <option value="">-- Select technician --</option>
                      {matchingTechs.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <Button variant="secondary" onClick={() => setAssignModal(null)}>Cancel</Button>
                  <Button
                    variant="primary"
                    disabled={!selectedTechId || updating === assignModal.id}
                    onClick={assignAndStart}
                  >
                    {updating === assignModal.id ? <Spinner size={18} /> : 'Assign & Start Repair'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )
      })()}

      {/* Add Repair Modal */}
      {addModal && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}
          onClick={closeAddModal}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: theme.colors.white, borderRadius: theme.radius.lg,
              maxWidth: '560px', width: '95%', maxHeight: '85vh', display: 'flex', flexDirection: 'column',
              boxShadow: theme.shadows.md
            }}
          >
            {/* Modal Header */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '20px', borderBottom: `1px solid ${theme.colors.grey200}`, flexShrink: 0
            }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>Add Repair</h2>
              <button
                onClick={closeAddModal}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: theme.colors.grey400 }}
              >
                &times;
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
              {/* Source Toggle */}
              <div style={{ display: 'flex', gap: '0', marginBottom: '16px', borderRadius: theme.radius.md, overflow: 'hidden', border: `1px solid ${theme.colors.grey300}` }}>
                {[
                  { key: 'hubspot', label: 'Deal ID' },
                  { key: 'shopify', label: 'Shopify Product ID' }
                ].map(s => (
                  <button
                    key={s.key}
                    onClick={() => { setAddSource(s.key); setAddLookupResult(null); setAddLookupError(''); setAddSelectedItems({}); setAddLookupId('') }}
                    style={{
                      flex: 1, padding: '10px 16px', border: 'none', cursor: 'pointer',
                      fontSize: '14px', fontWeight: 600,
                      background: addSource === s.key ? theme.colors.charcoal : theme.colors.white,
                      color: addSource === s.key ? theme.colors.white : theme.colors.charcoal
                    }}
                  >
                    {s.label}
                  </button>
                ))}
              </div>

              {/* Lookup Input */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                <input
                  type="text"
                  value={addLookupId}
                  onChange={e => setAddLookupId(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && doLookup()}
                  placeholder={addSource === 'hubspot' ? 'Enter deal ID...' : 'Enter Shopify Product ID...'}
                  style={{
                    flex: 1, padding: '10px 12px', fontSize: '14px',
                    border: `1px solid ${theme.colors.grey300}`, borderRadius: theme.radius.md,
                    outline: 'none'
                  }}
                />
                <Button variant="primary" disabled={addLooking || !addLookupId.trim()} onClick={doLookup}>
                  {addLooking ? <Spinner size={18} /> : 'Look Up'}
                </Button>
              </div>

              {/* Lookup Error */}
              {addLookupError && (
                <div style={{
                  padding: '12px 16px', background: theme.colors.redLight, color: theme.colors.crimson,
                  borderRadius: theme.radius.md, fontSize: '14px', marginBottom: '16px'
                }}>
                  {addLookupError}
                </div>
              )}

              {/* Lookup Results */}
              {addLookupResult && (
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: theme.colors.grey500, marginBottom: '8px' }}>
                    {addLookupResult.type === 'hubspot' ? `Deal: ${addLookupResult.dealName}` : 'Product Found'}
                  </div>

                  {/* Item Selection */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                    {addLookupResult.items.map(item => {
                      const selected = !!addSelectedItems[item.index]
                      return (
                        <div key={item.index}>
                          <div
                            onClick={() => toggleAddItem(item)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '12px',
                              padding: '12px 16px', borderRadius: theme.radius.md, cursor: 'pointer',
                              border: selected ? `2px solid ${theme.colors.greenOnions}` : `1px solid ${theme.colors.grey300}`,
                              background: selected ? theme.colors.greenLight : theme.colors.white
                            }}
                          >
                            <div style={{
                              width: '20px', height: '20px', borderRadius: '4px', flexShrink: 0,
                              border: selected ? `2px solid ${theme.colors.greenOnions}` : `2px solid ${theme.colors.grey300}`,
                              background: selected ? theme.colors.greenOnions : theme.colors.white,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              color: theme.colors.white, fontSize: '14px', fontWeight: 700
                            }}>
                              {selected && '✓'}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, fontSize: '14px' }}>{item.name}</div>
                              <div style={{ fontSize: '12px', color: theme.colors.grey500, marginTop: '2px' }}>
                                {item.productType && <span>{item.productType} • </span>}
                                Sell: {formatCurrency(item.sellPrice)} • Buy: {formatCurrency(item.buyPrice)}
                              </div>
                            </div>
                          </div>

                          {/* Issue Description for selected items */}
                          {selected && (
                            <div style={{ marginTop: '8px', marginLeft: '32px' }}>
                              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: theme.colors.grey500, marginBottom: '4px' }}>
                                What's wrong with it? *
                              </label>
                              <textarea
                                value={addSelectedItems[item.index]?.issueDescription || ''}
                                onChange={e => setItemIssue(item.index, e.target.value)}
                                placeholder="Describe the issue..."
                                rows={2}
                                style={{
                                  width: '100%', padding: '10px 12px', fontSize: '13px',
                                  border: `1px solid ${theme.colors.grey300}`, borderRadius: theme.radius.md,
                                  resize: 'vertical', outline: 'none'
                                }}
                              />
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            {addLookupResult && (
              <div style={{
                display: 'flex', gap: '12px', justifyContent: 'flex-end',
                padding: '16px 20px', borderTop: `1px solid ${theme.colors.grey200}`, flexShrink: 0
              }}>
                <Button variant="secondary" onClick={closeAddModal}>Cancel</Button>
                <Button
                  variant="success"
                  disabled={addSubmitting || Object.keys(addSelectedItems).length === 0}
                  onClick={submitAddRepairs}
                >
                  {addSubmitting ? <Spinner size={18} /> : `Add ${Object.keys(addSelectedItems).length} to Repairs`}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
          padding: '12px 24px', borderRadius: theme.radius.md,
          background: toast.type === 'error' ? theme.colors.crimson : theme.colors.greenOnions,
          color: theme.colors.white, fontSize: '0.9em', fontWeight: 500,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)', zIndex: 9999
        }}>
          {toast.message}
        </div>
      )}
    </div>
  )
}

export default Repairs
